import { makeAutoObservable, reaction } from 'mobx';
import { RulesType, ConfigType, BetType, ColumnsType, createColumns } from './config';
import { SportsListType } from 'src/AppState/sdk/staff';
import { BetTickerV2State } from 'src/domains/sportsbook/trading/view/bet-ticker/BetTickerV2.state';
import { createSettingsLabel, createSportsLabel, filterBets } from './BetTickerBox.helpers';
import { ToasterState } from 'src/AppState/ToasterState';
import { Common } from 'src/AppState/Common';

export interface SportOptionType {
    id: string;
    name: string;
}

export class BetTickerBoxState {
    public rules: RulesType | null;
    public custom: Boolean;
    public title: string;
    public config: ConfigType;

    public constructor(
        private readonly betTickerState: BetTickerV2State,
        private readonly common: Common
    ) {
        this.custom = false;
        this.rules = null;
        this.title = '';
        this.config = {
            channelDesktop: false,
            channelMobile: false,
            channelQuickbet: false,
            channelTelebetting: false,
        };

        this.betTickerState.allSports.get();

        makeAutoObservable(this);

        reaction(
            () => this.sportList,
            (sports) => {
                if (sports === 'error') {
                    ToasterState.get(this.common).setToast({
                        variant: 'error',
                        title: 'Error!',
                        message: 'There was an error retrieving sports list.',
                    });
                }
            }
        );
    }

    public setRules = (rules: RulesType | null): void => {
        this.rules = rules;
    };

    public setCustom = (custom: Boolean): void => {
        this.custom = custom;
    };

    public setTitle = (title: string): void => {
        this.title = title;
    };

    public setConfig = (config: ConfigType): void => {
        this.config = config;
    };

    public clearAll = (): void => {
        this.rules = null;
        this.custom = false;
        this.title = '';
        this.config = {
            channelDesktop: false,
            channelMobile: false,
            channelQuickbet: false,
            channelTelebetting: false,
        };
    };

    private get sportList(): SportsListType | 'loading' | 'error' {
        const res = this.betTickerState.allSports.get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error') {
            return 'error';
        }

        return res.value;
    }

    public get sportListOptions(): Array<SportOptionType> {
        if (this.sportList === 'loading' || this.sportList === 'error') {
            return [];
        }

        const sportsList = this.sportList.map((elem) => {
            return {
                id: elem.id,
                name: elem.name,
            };
        });

        return sportsList;
    }

    public get filteredBets(): BetType[] {
        const formattedData: BetType[] =
            this.rules?.rejectedBets === true
                ? JSON.parse(JSON.stringify(this.betTickerState.rejectedBetsData))
                : this.rules?.cashOutBets === true
                  ? JSON.parse(JSON.stringify(this.betTickerState.settledBetsData))
                  : JSON.parse(JSON.stringify(this.betTickerState.betsData));

        const filteredBets = filterBets(this.rules, formattedData);

        return filteredBets;
    }

    public get settingsLabel(): string | undefined {
        const settingsLabel = createSettingsLabel(this.rules);
        return settingsLabel;
    }

    public get sportsLabel(): string {
        const allSports = this.sportListOptions;
        const sportsLabel = createSportsLabel(allSports, this.rules);

        return sportsLabel;
    }

    public get columns(): ColumnsType {
        const allColumns = createColumns(this.config, this.rules ?? {});
        const columns =
            this.rules?.rejectedBets === true
                ? allColumns.filter((col) => !['payout', 'settledAt'].includes(col.id))
                : this.rules?.cashOutBets === true
                  ? allColumns.filter((col) => !['rejectReason'].includes(col.id))
                  : allColumns.filter((col) => !['rejectReason', 'payout', 'settledAt'].includes(col.id));

        return columns;
    }
}
